import { useCallback } from 'react';
import { SkippableFragment } from '@funtech-int/kp-graphql-sdk/yango-movies-www';

import { SkipButtonActivation } from '@kinopoisk-int/evgen-web-events/web/named_enums';

import { useSeek } from '@/scenarios/legacy-player/hooks/useSeek';

import {
    skippableFragmentTypeToSkipButtonTypeMap,
    useLogSkipButtonActivation,
} from '../../../../hooks/skin/use-switch-to-next-episode-with-analytics';
import { useAutoSkipActivator } from '../../next-episode-skip-button-group/hooks/use-auto-skip';

interface CommonParams {
    isAutoSkipEnabled: boolean;
    currentFragment: SkippableFragment;
}

interface Params extends CommonParams {
    onAutoSkip: () => void;
}

interface WrapperParams extends CommonParams {}

const useAutoSkip = ({ isAutoSkipEnabled, currentFragment, onAutoSkip }: Params) => {
    const { seekToTime } = useSeek();

    const skip = useCallback(() => {
        seekToTime(currentFragment.endTime);
        onAutoSkip?.();
    }, [currentFragment.endTime, onAutoSkip, seekToTime]);

    const animationActive = useAutoSkipActivator({
        skip,
        skippableFragment: currentFragment,
        isAutoSkipDisabled: !isAutoSkipEnabled,
    });

    return animationActive;
};

export const useAutoSkipWithAnalytics = (props: WrapperParams) => {
    const onButtonActivation = useLogSkipButtonActivation();

    const onAutoSkipWithLogging = useCallback(() => {
        onButtonActivation({
            skipSource:
                skippableFragmentTypeToSkipButtonTypeMap[props.currentFragment.type ?? 'UNKNOWN'],
            skippableFragmentType: SkipButtonActivation.ByTimer,
        });
    }, [onButtonActivation, props.currentFragment.type]);

    return useAutoSkip({
        ...props,
        onAutoSkip: onAutoSkipWithLogging,
    });
};
